import { useEffect, useState } from 'react';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { useWatch, Controller } from 'react-hook-form';
import { recursiveMap, recursiveFind } from '../../../utilities/treeHelpers';
import TreeViewModal from '../../../components/TreeViewModal';
import NodeIcon from '../../../components/NodeIcon';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { parseUserInfo, transErrorMessage } from '../../../utilities/common';
import { getSharedUnitFamiliesBetweenProfilesApi, getCompatibleEmissionProfileSourcesApi } from '../emissionProfileApi';

const EmissionOtherSourceLink = ({ control, setValue, optEnabledCustomFactor, customEmissionFactors, tree, emissionProfile, fullFuelCycleLinkedId }) => {
  const { t } = useTranslation();

  const [showTreeSelector, setShowTreeSelector] = useState(false);
  const [selectedNodes, setSelectedNodes] = useState(null);
  const [loadingIds, setLoadingIds] = useState(new Set());
  const { orgId } = parseUserInfo(useSelector((state) => state.users.user));

  const closeModal = () => setShowTreeSelector(false);

  const eprflsrcOtherLinkedIds = useWatch({
    control,
    name: 'eprflsrcOtherLinkedIds'
  });

  const otherLinked = useWatch({
    control,
    name: 'otherLinked'
  });

  const [compatibleIds, setCompatibleIds] = useState(null);
  const [isLoadingCompatible, setIsLoadingCompatible] = useState(false);

  const openModal = async () => {
    setCompatibleIds(null);
    setIsLoadingCompatible(true);
    setShowTreeSelector(true);
    try {
      const ids = await getCompatibleEmissionProfileSourcesApi(orgId, emissionProfile);
      setCompatibleIds(new Set(ids));
    } catch (e) {
      setCompatibleIds(null);
      toast.error(transErrorMessage(t, e));
    } finally {
      setIsLoadingCompatible(false);
    }
  };

  const fullFuelCycleId = typeof fullFuelCycleLinkedId === 'object' && fullFuelCycleLinkedId !== null
    ? fullFuelCycleLinkedId.eprflsrcId
    : fullFuelCycleLinkedId;

  const pruneTree = (node) => {
    if (node.eprflsrcId) {
      return compatibleIds.has(node.eprflsrcId) && node.eprflsrcId !== fullFuelCycleId ? node : null;
    }
    const children = (node.children ?? []).map(pruneTree).filter(Boolean);
    return children.length ? { ...node, children } : null;
  };
  const filteredTree = tree && (compatibleIds || isLoadingCompatible) && !isLoadingCompatible ? (pruneTree(tree) ?? { ...tree, children: [] }) : { ...(tree ?? {}), children: [] };

  const setSelectedTarget = (n, removeIdx = null) => {
    const newNodes = [...selectedNodes ?? []];
    if (removeIdx === null) {
      newNodes.push(n);
    } else {
      newNodes.splice(removeIdx, 1);
    }
    setValue(
      'eprflsrcOtherLinkedIds',
      newNodes.map(n => n.eprflsrcId), { shouldDirty: true },
    );
    setSelectedNodes(newNodes);
  };

  const handleChangeLinkedEps = async (n, idx) => {
    if (idx !== -1) {
      setSelectedTarget(n, idx);
      return;
    }
    try {
      setLoadingIds(prev => new Set(prev).add(n?.eprflsrcId));
      const data = {
        ...emissionProfile,
        targetEpsId: n?.eprflsrcId
      };
      const shareUnits = await getSharedUnitFamiliesBetweenProfilesApi(orgId, data);
      if (shareUnits.length) {
        setSelectedTarget(n);
      } else {
        toast.error(t('Cannot link to the selected emission profile source: no matching unit families found.'));
      }
    } catch (e) {
      toast.error(transErrorMessage(t, e));
    } finally {
      setLoadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(n?.eprflsrcId);
        return newSet;
      });
    }
  };
  const recursiveTree = recursiveMap(filteredTree, (n) => {
    if (n.eprflId) {
      return {
        value: n.eprflId,
        labelRaw: n.eprflName,
        label: <span>{n.eprflName}</span>,
        isActive: false
      };
    }
    if (n.eprflcatId) {
      return {
        value: n.eprflcatId,
        labelRaw: n.eprflcatName,
        label: (
          <span>
            <NodeIcon node={n} />
            {n.eprflcatName}
          </span>
        ),
        showCheckbox: true,
        isActive: false
      };
    }
    if (n.eprflsrcId) {
      if (emissionProfile && n.eprflsrcId === emissionProfile?.eprflsrcId) {
        return {
          value: n.eprflsrcId,
          labelRaw: n.eprflsrcName,
          label: (<span>{n.eprflsrcName}</span>),
          isActive: true,
        };
      }
      const idx = (selectedNodes || []).findIndex(item => item.eprflsrcId === n.eprflsrcId);
      return {
        value: n.eprflsrcId,
        labelRaw: n.eprflsrcName,
        label: (
          <span onClick={() => handleChangeLinkedEps(n, idx)}>
            <Form.Check
              disabled={loadingIds.has(n.eprflsrcId)}
              custom
              checked={idx !== -1}
              label={n.eprflsrcName}
            />
          </span>
        ),
        isActive: eprflsrcOtherLinkedIds?.length && eprflsrcOtherLinkedIds.includes(n.eprflsrcId)
      };
    }
  });
  recursiveTree.isRoot = true;

  useEffect(() => {
    if (tree && eprflsrcOtherLinkedIds && eprflsrcOtherLinkedIds.length && selectedNodes === null) {
      const newNodes = eprflsrcOtherLinkedIds.map(id => {
        const found = recursiveFind(tree, (n) => n.eprflsrcId === parseInt(id));
        return found || { eprflsrcName: 'Error' };
      });
      setSelectedNodes(newNodes);
    }
  }, [eprflsrcOtherLinkedIds, selectedNodes, tree, setValue]);

  const renderSelectedNodes = () => {
    if (!selectedNodes || !selectedNodes.length) {
      return null;
    }
    return selectedNodes.map((item, idx) => (
      <Col md={12} style={{ paddingLeft: 0 }}>
        <span key={`selected-node-${idx}`} className="ml-3">
          {item.eprflsrcName}
        </span>
      </Col>
    ));
  };
  const isDisabled = !emissionProfile.eprflsrcEsrcId && ((optEnabledCustomFactor && !customEmissionFactors?.length)
    || (!optEnabledCustomFactor && !emissionProfile.eprflsrcConsumptionUnit));
  return (
    <>
      <Col>
        <Form.Group className="custom-form-control">
          <Controller
            name="otherLinked"
            control={control}
            render={({ field }) => {
              const parsed = field.value === 'Y' ? true : false;
              const format = () => {
                field.onChange(field.value === 'Y' ? 'N' : 'Y');
                setValue('eprflsrcOtherLinkedIds', []);
                setSelectedNodes([]);
              };
              return (
                <Form.Check
                  custom
                  id="otherLinked"
                  onChange={format}
                  checked={parsed}
                  disabled={isDisabled}
                  label={t('Other Linked EPS')}
                />
              );
            }}
          />
        </Form.Group>
      </Col>
      {otherLinked === 'Y' && (
        <Col>
          <Form.Group className="custom-form-control">
            <Form.Label>{t('Scope 1, 2 & 3 Linked EPS')}</Form.Label>
            <Row>
              <Col style={{ flexGrow: 0 }}>
                <Button 
                  disabled={isDisabled} 
                  onClick={openModal} 
                  variant="outline-secondary"
                >
                  {t('Select')}
                </Button>
              </Col>
              <Col>
                <Row>
                  {renderSelectedNodes()}
                </Row>
              </Col>
            </Row>
          </Form.Group>
          <TreeViewModal
            tree={recursiveTree}
            closeModal={closeModal}
            show={showTreeSelector}
            title={t('Select Emission Profile')}
          />
        </Col>
      )}
    </>
  );
};

export default EmissionOtherSourceLink;
